<template>
  <div class="photo-uploader">
    <div class="photo-uploader__label">{{ label }}<span v-if="required" class="required__icon">*</span></div>
    <div
      :class="[
        'photo-uploader__wrapper',
        isDragStarted ? 'photo-uploader__wrapper--drag-started' : '',
        hasErrors && !loaderStart ? 'photo-uploader__wrapper--error' : '',
      ]">
      <input
        ref="input"
        class="photo-uploader__input"
        type="file"
        accept="image/*"
        title=""
        @change="uploadFile"
        :style="{ 'z-index': contentUrl !== '' ? '-1' : '7' }"
        @dragenter="isDragStarted = true"
        @dragleave="isDragStarted = false" />

      <div class="photo-uploader__loader" v-show="loaderStart">
        <Loader />
        <div class="photo-uploader__loader--text">Uploading..</div>
      </div>

      <div
        class="photo-uploader__layer"
        v-if="isDragStarted"
        :style="{ 'background-color': $vuetify.theme.dark ? '#544f7c' : '#D6D6D6' }">
        <img src="@/assets/icons/upload.svg" class="photo-uploader__icon" alt="Upload image" />
      </div>

      <div class="photo-uploader__content w-full">
        <div v-if="contentUrl === '' && !loaderStart">
          <div class="photo-uploader__text">
            <span class="photo-uploader__text--title">
              {{ $t("generic.drop_image") }}
            </span>
            <span class="photo-uploader__text--subtitle">
              {{ $t("generic.or") }}
            </span>
          </div>
          <div class="photo-uploader__btn">
            <v-btn class="btn-input-file" color="primary" outlined>
              <span>{{ $t("generic.click_to_browse") }}</span>
            </v-btn>
          </div>
        </div>

        <div class="photo-uploader__photos" v-if="contentUrl && !loaderStart">
          <!-- TODO: Alt tag for image -->
          <img :src="contentUrl" alt="img" />
        </div>
      </div>
    </div>
    <div class="v-messages mt-2 error--text" v-if="hasErrors && !loaderStart">
      <div class="v-messages__wrapper">
        <div class="v-messages__message">
          <template v-if="ssrError">
            {{ ssrError }}
          </template>
          <template v-for="error in commonProps.errorMessages">
            {{ error }}
          </template>
        </div>
      </div>
    </div>
    <div class="photo-uploader__actions" v-if="contentUrl !== ''">
      <div class="d-flex">
        <span class="photo-uploader__actions--name">{{ imageName }}</span>
        <span class="photo-uploader__actions--size">{{ imageSize }}</span>
      </div>
      <div class="d-flex ml-auto">
        <v-btn type="link" :ripple="false" class="ml-3 change-img-btn" plain @click="downloadImage" title="Download image">
          <v-icon class="mr-1">mdi-download</v-icon>
          {{ $t("generic.buttons.download") }}
        </v-btn>
        <v-btn type="link" :ripple="false" class="ml-3 change-img-btn" plain @click.prevent="$refs.input.click()">
          <v-icon class="mr-1">mdi-refresh</v-icon>
          {{ $t("generic.buttons.change_image") }}
        </v-btn>
        <v-btn type="link" :ripple="false" class="white-color pr-0" color="error" plain @click="removeImage">
          <v-icon class="mr-1">mdi-trash-can-outline</v-icon>
          {{ $t("generic.buttons.delete_image") }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
  import Input from "@inulti/vuetify-admin/src/mixins/input";
  import Loader from "@/components/custom/CircleLoader.vue";
  import { ref, onMounted, toRefs, watch, computed, getCurrentInstance } from "@vue/composition-api";
  import { sendToServer, downloadMediaObject } from "@/utility/mediaObjectUploadHelper";
  import { getFromServer } from "@/utility/fetchMediaObject";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import definedAbility from "@/ability";

  export default {
    name: "VaImageDragAndDropInput",
    components: {
      Loader,
    },
    mixins: [Input],
    props: {
      label: {
        type: String,
        required: false,
        default: "Upload file",
      },
      itemId: {
        type: String,
        required: false,
        default: null,
      },
      onlyShowImg: {
        type: Boolean,
        required: false,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      displayErrors: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:photo"],
    setup(props, { emit }) {
      const vm = getCurrentInstance().proxy;

      const { itemId } = toRefs(props);
      const isDragStarted = ref(false);
      const loaderStart = ref(false);

      const contentUrl = ref("");
      const imageName = ref("");
      const imageSize = ref("");
      const currentFileId = ref(props.itemId || "");
      const initialImageSelected = ref(false);
      // Server-side error
      const ssrError = ref(null);
      const ability = definedAbility();

      const hasErrors = computed(() => {
        // If image is required, input had an image added at some point (it was removed) and currently there's no image uploaded
        if (props.required && initialImageSelected.value && contentUrl.value === "") return true;
        // If image is required and input don't have an image added on form submit
        if (props.required && props.displayErrors && contentUrl.value === "") return true;
        // If there's a server-side error
        if (ssrError.value) return true;
        if (vm.commonProps.errorMessages.length > 0) return true;

        return false;
      });

      const uploadFile = async fileToUpload => {
        isDragStarted.value = false;
        loaderStart.value = true;
        ssrError.value = null;
        imageName.value = fileToUpload.target.files[0]?.name;
        imageSize.value = imageSizeInMb(fileToUpload.target.files[0]?.size);
        try {
          const res = await sendToServer(fileToUpload.target.files[0]);
          currentFileId.value = res["@id"] || "";
          if (ability.can("upload", "media object to inulti server")) {
            contentUrl.value = process.env.VUE_APP_API + res["contentUrl"];
          } else {
            contentUrl.value = res["contentUrl"];
          }
          emit("update:photo", res);
          // Update image size and name only on successful upload
        } catch (e) {
          ssrError.value = e.description;
        } finally {
          loaderStart.value = false;
        }
      };

      const imageSizeInMb = bytes => {
        return (bytes / 1000000).toFixed(2) + " MB";
      };

      const imageRemoved = ref(false);
      const removeImage = () => {
        contentUrl.value = "";
        imageRemoved.value = true;
        imageName.value = "";
        initialImageSelected.value = true;
        ssrError.value = null;
        emit("update:photo", "");
      };

      const loadPreviewImage = async () => {
        if (!imageRemoved.value && itemId.value) {
          loaderStart.value = true;
          await getFromServer(itemId.value).then(res => {
            if (ability.can("upload", "media object to inulti server")) {
              contentUrl.value = process.env.VUE_APP_API + res["contentUrl"];
            } else {
              contentUrl.value = res["contentUrl"];
            }
            if (!imageName.value && res["contentUrl"]) {
              imageName.value = res["contentUrl"].split("/").pop();
            }
            loaderStart.value = false;
          });
        }
      };

      watch(
        () => props.itemId,
        (currInput, prevInput) => {
          if (currInput !== null && currInput !== prevInput) {
            currentFileId.value = currInput;
            imageRemoved.value = false;
            loadPreviewImage();
          } else if (currInput === null) {
            removeImage();
          }
        },
      );

      const downloadImage = async () => {
        const id = currentFileId.value || itemId.value;
        if (!id) return;
        try {
          await downloadMediaObject(id, imageName.value);
        } catch (e) {
          vm.$admin.toast.error(formErrorMessage(e, vm.$t("generic.error")));
        }
      };

      onMounted(() => {
        loadPreviewImage();
      });

      return {
        uploadFile,
        removeImage,
        downloadImage,
        contentUrl,
        imageName,
        imageSize,
        isDragStarted,
        loaderStart,
        initialImageSelected,
        hasErrors,
        ssrError,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .v-messages {
    &__wrapper {
      margin-top: 8px;
    }
  }

  .photo-uploader {
    .required__icon {
      color: get-color("primary");
    }
    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
      margin-bottom: 4px;
    }

    &__wrapper {
      position: relative;
      text-align: center;
      min-height: 220px;
      display: flex;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      border-radius: 4px;
      flex-direction: column;
      background-color: get-color("element-bg-200");
      border: 1px dashed get-color("border");

      &--drag-started {
        border-color: get-color("primary");
      }
      &--error {
        border-color: get-color("error");
      }
    }

    &__input {
      cursor: pointer;
      position: absolute;
      z-index: 2;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      opacity: 0;
    }

    &__text {
      display: flex;
      flex-direction: column;
      &--title {
        @include text-body-2-bold();
        color: get-color("body");
        margin-bottom: 8px;
      }
      &--subtitle {
        @include text-body-2();
        color: get-color("body");
        margin-bottom: 8px;
      }
    }

    &__photos {
      padding: 15px;

      img {
        max-height: 220px;
        max-width: 100%;
      }
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      width: 100%;
      margin-top: 8px;

      &--name {
        @include text-caption-bold();
        color: get-color("body");
        margin-right: 5px;
      }

      &--size {
        @include text-caption();
        color: get-color("body");
      }
    }

    &__layer {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 6;
      border-radius: 4px;
      margin: 15px;
    }

    &__loader {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      z-index: 6;

      &--text {
        @include text-body-2-bold();
        color: get-color("body");
      }
    }

    .change-img-btn {
      color: get-color("secondary-btn");

      i {
        transition: none;
      }

      &:hover {
        color: get-color("same-hover");
      }
    }
  }
</style>
